import axios, { AxiosError, AxiosRequestConfig, Method } from 'axios'
import { TFunction } from 'i18next'
import queryString from 'query-string'
import { isC1ApiError } from './c1ApiTypes'

export class CancelRequestError extends Error {
  public constructor(message: string) {
    super(message)
    this.name = 'CancelRequestError'

    Object.setPrototypeOf(this, CancelRequestError.prototype)
  }
}

export class ApiError extends Error {
  public response: $TSFIXME
  public constructor({ message = 'API_ERROR', response = null, ...params }: $TSFIXME) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TODO: remove type assertion
    super(params as $TSFIXME)

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError)
    }

    this.message = message
    this.response = response

    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error#Custom_Error_Types
    Object.defineProperty(this, 'toString', {
      value: () => this.message,
    })
  }
}

type ApiCall<Params, Result> = (parameters?: Params, config?: AxiosRequestConfig<Params>) => Promise<Result>

export const createApiCall = <Params, Result>(url: string, method: Method = 'get', responseTransformer: ((data: $TSFIXME) => Result) = data => data): ApiCall<Params, Result> => async (
  parameters,
  config = {},
): Promise<Result> => {
  try {
    // If method is get, put the parameters in the query, else, put them in the body
    const defaultParametersLocation = method === 'get' ? 'params' : 'data'
    const axiosConfig: AxiosRequestConfig<Params> = {
      paramsSerializer: {
        serialize: params => queryString.stringify(params),
      },
      ...config,
      url,
      method,
    }
    if (parameters) {
      axiosConfig[defaultParametersLocation] = parameters
    }

    const response = await axios.request<$TSFIXME>(axiosConfig)
    return responseTransformer(response.data)
  } catch (error) {
    throw new ApiError(error)
  }
}

export const createGetApiCall = <Params, Result>(route: string, responseTransformer?: (data: $TSFIXME) => Result): ApiCall<Params, Result> => createApiCall(route, 'get', responseTransformer)
export const createPostApiCall = <Params, Result>(route: string, responseTransformer?: (data: $TSFIXME) => Result): ApiCall<Params, Result> => createApiCall(route, 'post', responseTransformer)
export const createPutApiCall = <Params, Result>(route: string, responseTransformer?: (data: $TSFIXME) => Result): ApiCall<Params, Result> => createApiCall(route, 'put', responseTransformer)

export const createLocalStorageGetCall = (key: string) => (): unknown => {
  const rawValue = window.localStorage.getItem(key)
  return rawValue ? JSON.parse(rawValue) : null
}

export const createLocalStorageSetCall = <T>(key: string) => (value: T): T => {
  window.localStorage.setItem(key, JSON.stringify(value))
  return value
}

export const createLocalStorageDeleteCall = (key: string): void => {
  window.localStorage.removeItem(key)
}

export interface ParsedAxiosError {
  errorMessage: string,
  requestId?: string,
}

export function getErrorMessageFromCaughtAxiosError(t: TFunction, error: unknown): ParsedAxiosError {
  // based on official axios error handling example https://axios-http.com/docs/handling_errors
  // logging gets sent to DataDog while the return statements allow the UI to show an error message to the user
  if (!(error instanceof AxiosError)) {
    // eslint-disable-next-line no-console -- TODO: remove this eslint-disable or replace this eslint-disable comment with a proper justification
    console.error('Unhandled error thrown from Axios. Error:', error)
    return { errorMessage: t('unknownClientError') }
  } else if (error.response) {
    // Server responded successfully
    if (isC1ApiError(error.response.data)) {
      // Standard app server error response body detected
      return error.response.data
    } else {
      // App server error body was non-standard
    // eslint-disable-next-line no-console -- TODO: remove this eslint-disable or replace this eslint-disable comment with a proper justification
      console.error('Unhandled server error response. Response:', error.response)
      return { errorMessage: t('unknownServerError') }
    }
  } else if (error.request) {
    // Axios sent a request, but no server response was received
    // eslint-disable-next-line no-console -- TODO: remove this eslint-disable or replace this eslint-disable comment with a proper justification
    console.error('No response received from server. Request:', error.request)
    return { errorMessage: t('connectionError') }
  } else {
    // Axios failed to construct the request
    // eslint-disable-next-line no-console -- TODO: remove this eslint-disable or replace this eslint-disable comment with a proper justification
    console.error('Axios failed to construct request. AxiosError:', error)
    return { errorMessage: t('unknownClientError') }
  }
}

export interface RtkQueryError<T> {
  status: number,
  data: T,
}

export function isRtkQueryError(error: unknown): error is RtkQueryError<unknown> {
  return typeof error === 'object'
    && error !== null
    && 'status' in error
    && typeof error.status === 'number'
    && 'data' in error
}

export function getErrorMessageFromCaughtRtkQueryError(t: TFunction, error: unknown): ParsedAxiosError {
  if (!isRtkQueryError(error)) {
    return { errorMessage: t('unknownClientError') }
  } else if (!isC1ApiError(error.data)) {
    return { errorMessage: t('unknownServerError') }
  } else {
    return { errorMessage: error.data.errorMessage, requestId: error.data.requestId }
  }
}
